import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { List, Save, Loader2, Zap, Plus, X, Image } from "lucide-react";

type Listing = {
  id: string;
  title: string;
  teaser: string | null;
  description: string | null;
  price: number;
  revenue: number | null;
  ebitda: number | null;
  sector: string;
  status: string;
  location_state: string | null;
  location_suburb: string | null;
  listing_number: string | null;
  image_url: string | null;
  images: string[] | null;
  featured: boolean | null;
  requires_nda: boolean;
  custom_image: boolean;
};

const SECTORS = [
  "Hospitality",
  "Retail",
  "Services",
  "Health & Beauty",
  "Automotive",
  "Professional Services",
  "Food & Beverage",
  "Education & Training",
  "Transport & Logistics",
  "Manufacturing",
  "Technology",
  "Other"
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];

export function AdminListingEditor() {
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  const loadAllListings = async () => {
    setSearching(true);
    try {
      const { data, error } = await supabase
        .from("listings")
        .select("id, title, teaser, description, price, revenue, ebitda, sector, status, location_state, location_suburb, listing_number, image_url, images, featured, requires_nda, custom_image")
        .order("title", { ascending: true });

      if (error) throw error;

      setListings(data || []);
      toast.success(`Loaded ${data?.length || 0} listings`);
      setSelectedListing(null);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!selectedListing) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("listings")
        .update({
          title: selectedListing.title,
          teaser: selectedListing.teaser,
          description: selectedListing.description,
          price: selectedListing.price,
          revenue: selectedListing.revenue,
          ebitda: selectedListing.ebitda,
          sector: selectedListing.sector,
          status: selectedListing.status,
          location_state: selectedListing.location_state,
          location_suburb: selectedListing.location_suburb,
          image_url: selectedListing.image_url,
          images: selectedListing.images,
          featured: selectedListing.featured,
          requires_nda: selectedListing.requires_nda,
          custom_image: (selectedListing.images && selectedListing.images.length > 0) || !!selectedListing.image_url,
        })
        .eq("id", selectedListing.id);

      if (error) throw error;

      toast.success("Listing updated successfully");
      
      // Update the listing in the list
      setListings(prev => prev.map(l => l.id === selectedListing.id ? selectedListing : l));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (!selectedListing) return;
    const currentImages = selectedListing.images || [];
    setSelectedListing({ ...selectedListing, images: [...currentImages, ""] });
  };

  const updateImage = (index: number, url: string) => {
    if (!selectedListing) return;
    const newImages = [...(selectedListing.images || [])];
    newImages[index] = url;
    setSelectedListing({ ...selectedListing, images: newImages });
  };

  const removeImage = (index: number) => {
    if (!selectedListing) return;
    const newImages = (selectedListing.images || []).filter((_, i) => i !== index);
    setSelectedListing({ ...selectedListing, images: newImages });
  };

  const updateField = (field: keyof Listing, value: any) => {
    if (!selectedListing) return;
    setSelectedListing({ ...selectedListing, [field]: value });
  };

  const handleMakeLive = async () => {
    if (!selectedListing) return;

    setActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-activate-listing", {
        body: { listing_id: selectedListing.id }
      });

      if (error) throw error;

      toast.success(`Listing activated! Number: ${data.listing_number}`);
      
      // Update local state
      const updatedListing = { ...selectedListing, status: "live", listing_number: data.listing_number };
      setSelectedListing(updatedListing);
      setListings(prev => prev.map(l => l.id === selectedListing.id ? updatedListing : l));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setActivating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Listings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Load All Button */}
        <Button onClick={loadAllListings} disabled={searching} className="w-full">
          {searching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <List className="h-4 w-4 mr-2" />}
          Load All Listings (A-Z)
        </Button>

        {/* Search Results */}
        {listings.length > 0 && !selectedListing && (
          <div className="space-y-2">
            <Label>Select a listing to edit:</Label>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {listings.map((listing) => (
                <div
                  key={listing.id}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => setSelectedListing(listing)}
                >
                  <div className="font-medium">{listing.title}</div>
                  <div className="text-sm text-muted-foreground">
                    #{listing.listing_number} • {listing.status} • ${listing.price.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Form */}
        {selectedListing && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Editing: {selectedListing.title}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedListing(null)}>
                Back to results
              </Button>
            </div>

            <div className="grid gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={selectedListing.title}
                  onChange={(e) => updateField("title", e.target.value)}
                />
              </div>

              <div>
                <Label>Teaser</Label>
                <Textarea
                  value={selectedListing.teaser || ""}
                  onChange={(e) => updateField("teaser", e.target.value)}
                  rows={2}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={selectedListing.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.price}
                    onChange={(e) => updateField("price", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>Revenue ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.revenue || ""}
                    onChange={(e) => updateField("revenue", e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>EBITDA ($)</Label>
                  <Input
                    type="number"
                    value={selectedListing.ebitda || ""}
                    onChange={(e) => updateField("ebitda", e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={selectedListing.status} onValueChange={(v) => updateField("status", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sector</Label>
                  <Select value={selectedListing.sector} onValueChange={(v) => updateField("sector", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((sector) => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State</Label>
                  <Select value={selectedListing.location_state || ""} onValueChange={(v) => updateField("location_state", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Suburb</Label>
                <Input
                  value={selectedListing.location_suburb || ""}
                  onChange={(e) => updateField("location_suburb", e.target.value)}
                />
              </div>

              {/* Images Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Images ({(selectedListing.images || []).length})
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addImage}>
                    <Plus className="h-4 w-4 mr-1" /> Add Image
                  </Button>
                </div>
                
                {(selectedListing.images || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No images added yet. Click "Add Image" to add URLs.</p>
                )}
                
                <div className="space-y-2">
                  {(selectedListing.images || []).map((img, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-1">
                        <Input
                          placeholder="Enter image URL..."
                          value={img}
                          onChange={(e) => updateImage(index, e.target.value)}
                        />
                        {img && (
                          <img 
                            src={img} 
                            alt={`Preview ${index + 1}`} 
                            className="h-16 w-24 object-cover rounded border"
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                          />
                        )}
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeImage(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Primary Image URL (legacy)</Label>
                <Input
                  value={selectedListing.image_url || ""}
                  onChange={(e) => updateField("image_url", e.target.value)}
                  placeholder="Optional - use images array above instead"
                />
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={selectedListing.featured || false}
                    onCheckedChange={(v) => updateField("featured", v)}
                  />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={selectedListing.requires_nda}
                    onCheckedChange={(v) => updateField("requires_nda", v)}
                  />
                  <Label>Requires NDA</Label>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                {selectedListing.status === "draft" && (
                  <Button onClick={handleMakeLive} disabled={activating} variant="default" className="bg-green-600 hover:bg-green-700">
                    {activating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                    Make Live
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
